import React, { useState } from 'react';
import axios from 'axios';
import { Clock, Lock, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useClockGate } from '../../hooks/useClockGate';
import { formatApiErrorDetail } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Route wrapper for the working schedule. Field/clock-required employees must
// have an ACTIVE clock entry (verified against the backend) before any schedule
// data renders. Deep-link / refresh / PWA safe: the gate always mounts first and
// re-checks canonical status. Office/admin roles pass straight through.
const ScheduleClockGate = ({ children }) => {
  const { required, clockedIn, loading, refresh } = useClockGate();
  const [clocking, setClocking] = useState(false);
  const [error, setError] = useState('');
  const [needReason, setNeedReason] = useState(false);
  const [reason, setReason] = useState('no_shift_scheduled');

  if (loading || clockedIn === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="schedule-gate-loading">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!required || clockedIn === true) return children;

  const handleClockIn = async (withReason = null) => {
    setClocking(true);
    setError('');
    try {
      // Guard against a stale UI: if the backend already has an open entry, don't
      // create a second one — just unlock.
      const already = await refresh();
      if (already) { setClocking(false); return; }
      const body = { action: 'in', appointment_id: null };
      if (withReason) body.fallback_reason = withReason;
      await axios.post(`${API}/timeclock`, body, { withCredentials: true });
      window.dispatchEvent(new Event('nexflow:clock-changed'));
      const ok = await refresh();
      if (!ok) setError('Clock-in could not be confirmed. Please try again.');
    } catch (err) {
      // Backend may report "already clocked in" — treat as success by re-checking.
      const ok = await refresh();
      if (ok) { setClocking(false); return; }
      const detail = formatApiErrorDetail(err.response?.data?.detail) || 'Clock-in failed. Please try again.';
      // No scheduled shift today → ask for a fallback reason (audited), then retry.
      if (/fallback reason is required/i.test(detail)) {
        setNeedReason(true);
      } else {
        setError(detail);
      }
    } finally {
      setClocking(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-16" data-testid="schedule-clock-gate">
      <div className="max-w-md w-full bg-[#141414] border border-[#262626] rounded-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-5">
          <Lock className="w-7 h-7 text-[#D4AF37]" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Clock In Required</h1>
        <p className="text-[#A3A3A3] text-sm mb-6" data-testid="schedule-gate-message">
          You must clock in before viewing your scheduled work.
        </p>
        {error && (
          <p className="text-red-300 text-sm mb-4 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="schedule-gate-error">
            {error}
          </p>
        )}
        {needReason ? (
          <div className="mb-4 text-left" data-testid="schedule-gate-reason">
            <p className="text-[#A3A3A3] text-xs mb-2">No scheduled shift found for today. Pick a reason to clock in:</p>
            <select
              data-testid="schedule-gate-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm px-3 py-2 mb-3"
            >
              <option value="no_shift_scheduled">No shift scheduled</option>
              <option value="emergency_call">Emergency call</option>
              <option value="forgot_to_assign">Forgot to assign shift</option>
              <option value="office_admin_approved">Office/admin approved</option>
              <option value="other">Other</option>
            </select>
            <Button
              data-testid="schedule-gate-clock-in-reason-btn"
              onClick={() => handleClockIn(reason)}
              disabled={clocking}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-12 text-base"
            >
              {clocking ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Clocking in…</> : <><Clock className="w-5 h-5 mr-2" /> Clock In &amp; View Schedule</>}
            </Button>
          </div>
        ) : (
          <Button
            data-testid="schedule-gate-clock-in-btn"
            onClick={() => handleClockIn(null)}
            disabled={clocking}
            className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-12 text-base"
          >
            {clocking ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Clocking in…</> : <><Clock className="w-5 h-5 mr-2" /> Clock In &amp; View Schedule</>}
          </Button>
        )}
        <p className="text-[10px] text-[#525252] mt-4">
          The rest of the Hub (Time Off, My Benefits, timesheet, profile) stays available while you&apos;re clocked out.
        </p>
      </div>
    </div>
  );
};

export default ScheduleClockGate;
